/**
 * STANDARDFELTENE PÅ PERSONEN — felles ordforråd for personfelt, skjema og personskjemaet (A1/H1).
 *
 * ================================================================================================
 * FASTE NØKLER, IKKE STAVEMÅTE (QA13 #35, Atles vedtak 253.1).
 *
 * «Tskjorte-størrelse» på skjemaet og «T-skjortestørrelse» i personfeltene fikk hver sin nøkkel og
 * møttes aldri. Standardfeltene har derfor en `system_key` som Tvitvi eier, og verdien ligger på
 * personen (`person_details` når personen har konto, ellers `person_facts` for organisasjonen).
 * Et skjemafelt kobles til personfeltet med id (`signup_form_fields.person_field_id`).
 *
 * Nøklene bruker understrek med vilje: `slugify` lager bare bindestrek, så et eget felt kan aldri få
 * samme nøkkel som et standardfelt.
 *
 * Navn, e-post og telefon er også standardopplysninger, men de er ikke personfelt: skjemaet spør om
 * dem alltid (`app.submit_signup` tar dem som egne argumenter), og personskjemaet har dem øverst.
 */
import { z } from 'zod';

import { TSHIRT_SIZES } from './person-card.js';

/**
 * Standardfeltene som finnes som rader i `person_fields` (med hengelås).
 *
 * `date_of_birth` er av som standard (253.13): raden lages arkivert, og organisasjonen slår den på
 * ved å hente den tilbake.
 */
export const PERSON_SYSTEM_KEYS = [
  'allergens_diets',
  'tshirt_size',
  'emergency_contact',
  'driving_licence',
  'date_of_birth',
] as const;
export const personSystemKeySchema = z.enum(PERSON_SYSTEM_KEYS);
export type PersonSystemKey = (typeof PERSON_SYSTEM_KEYS)[number];

/**
 * De sensitive standardfeltene (1077, PERSONTYPER-BESTILLING §1): pårørende og fødselsdato. Vises bare
 * for organisasjonens egne folk med rolle minst plattformens grense, og for personen selv. Også når
 * svaret står på et skjemaspørsmål KOBLET til personfeltet. Basen har den samme lista i
 * `app.person_field_is_sensitive`.
 */
export const SENSITIVE_SYSTEM_KEYS: readonly PersonSystemKey[] = [
  'emergency_contact',
  'date_of_birth',
];

export function isSensitiveSystemKey(key: string | null | undefined): boolean {
  return key !== null && key !== undefined && (SENSITIVE_SYSTEM_KEYS as readonly string[]).includes(key);
}

/**
 * «ALLERGIER OG KOSTHOLD» ER ÉN TEKST (A4, Atle 22.10, sak 271.3 a).
 *
 * Til 22.10 var det koder (14 allergener, 5 kosthold) og et notat (QA13 #61, «kodene kjøkkenet kan
 * telle»). Det er opphevet: svaret står slik personen skrev det, og kjøkkenlista teller like svar ved
 * å dele teksten på komma, semikolon og «og» (250.9) — `foodAnswerParts`.
 */
export const FOOD_TEXT_MAX = 1500;

/**
 * Delene i et matsvar, slik kjøkkenlista teller dem: delt på komma, semikolon, skråstrek og «og»/«and»,
 * uten tomme biter. «Gluten, melk og vegetar» er tre. Store og små bokstaver beholdes; den som teller,
 * slår sammen uten å se på dem.
 */
export function foodAnswerParts(text: string | null | undefined): string[] {
  if (text === null || text === undefined) return [];
  return text
    .split(/[,;/]|\s+og\s+|\s+and\s+/iu)
    .map((part) => part.trim().replace(/[.]+$/u, '').trim())
    .filter((part) => part.length > 0);
}

/** Nærmeste pårørende: navn, telefon og relasjon. */
export const emergencyContactValueSchema = z
  .object({
    name: z.string().trim().max(120).nullable().default(null),
    phone: z.string().trim().max(40).nullable().default(null),
    relation: z.string().trim().max(60).nullable().default(null),
  })
  .meta({ id: 'EmergencyContactValue' });
export type EmergencyContactValue = z.infer<typeof emergencyContactValueSchema>;

/**
 * Svaret på et skjemafelt som ER et standardfelt, etter feltets `system_key`.
 *
 * Basen (`app.submit_signup`) leser den samme formen og skriver den på personen; svaret står også
 * i `signup_answers` slik at oppsummeringen av skjemaet teller det.
 */
export const systemAnswerSchemas = {
  allergens_diets: z.string().trim().max(FOOD_TEXT_MAX),
  tshirt_size: z.string().trim().max(40),
  emergency_contact: emergencyContactValueSchema,
  driving_licence: z.string().trim().max(120),
  date_of_birth: z.iso.date(),
} as const satisfies Record<PersonSystemKey, z.ZodType>;

/**
 * Skjemafeltets type for hvert standardfelt — det `signup_form_fields.type` får når feltet legges
 * til fra personfeltlista. Typen styrer bare oppsummeringen; skjemaet tegner kontrollen etter
 * `systemKey`.
 */
export const SYSTEM_KEY_SIGNUP_TYPE = {
  allergens_diets: 'textarea',
  tshirt_size: 'select',
  emergency_contact: 'text',
  driving_licence: 'text',
  date_of_birth: 'date',
} as const satisfies Record<PersonSystemKey, string>;

/**
 * Valgene skjemafeltet bærer. Bare T-skjorta har en «utfyllingsform» (størrelsene som nedtrekk), og
 * svaret lagres som tekst likevel (A4). Null: feltet har ingen valg.
 */
export function systemKeyOptions(key: PersonSystemKey): readonly string[] | null {
  return key === 'tshirt_size' ? TSHIRT_SIZES : null;
}
